// services/frontend/app/features/DiscoverySettingsForms/components/ApplicationMapping/dataTransfer/aimExampleData.ts

import PizZip from 'pizzip';
import type {
  AimApplicationDraft,
  AimFlowDraft,
  AimWorkloadDraft,
} from '../../../model/aimDrafts';

const APP_FIN_ID = '0366c428-5a94-4dac-b4f8-144072a749de';
const APP_HR_ID = '6c18568a-6e70-4db3-ade9-9e6dda9f3fe0';
const WL_FIN_WEB_ID = '1344a79b-2cd8-405e-a7db-8514af583e41';
const WL_FIN_DB_ID = '96cace75-2c4b-46b2-b5cb-52091395dac2';
const WL_HR_APP_ID = 'bf18b511-cff6-4f9e-8b04-825cb1c51da0';

const EXAMPLE_APPS: AimApplicationDraft[] = [
  { id: APP_FIN_ID, name: 'Finance', environment: 'prod' },
  { id: APP_HR_ID, name: 'HR', environment: 'prod' },
];

const EXAMPLE_WORKLOADS: AimWorkloadDraft[] = [
  {
    id: WL_FIN_WEB_ID,
    applicationId: APP_FIN_ID,
    name: 'finance-web',
    ipAddress: '203.0.113.10',
    interfaces: ['203.0.113.10', '198.51.100.1'],
  },
  {
    id: WL_FIN_DB_ID,
    applicationId: APP_FIN_ID,
    name: 'finance-db',
    ipAddress: '203.0.113.11',
    interfaces: ['203.0.113.11', '203.0.113.12'],
  },
  {
    id: WL_HR_APP_ID,
    applicationId: APP_HR_ID,
    name: 'hr-app',
    ipAddress: '203.0.113.20',
    interfaces: ['203.0.113.20'],
  },
];

const EXAMPLE_FLOWS: AimFlowDraft[] = [
  {
    id: 'f62bc6a2-2479-48a7-8bd4-f6924a32c183',
    srcWorkloadId: WL_FIN_WEB_ID,
    dstWorkloadId: WL_FIN_DB_ID,
    srcIp: '203.0.113.10',
    dstIp: '203.0.113.11',
    srcPort: null,
    dstPort: 5432,
    protocol: 6,
  },
  {
    id: '55678583-b7e8-4aa2-b685-c690ef80536a',
    srcWorkloadId: WL_HR_APP_ID,
    dstWorkloadId: WL_FIN_WEB_ID,
    srcIp: '203.0.113.20',
    dstIp: '203.0.113.10',
    srcPort: null,
    dstPort: 443,
    protocol: 6,
  },
];

const APP_HEADERS = ['id', 'name', 'environment'];
const WORKLOAD_HEADERS = [
  'id',
  'applicationId',
  'name',
  'ipAddress',
  'interfaces (list of IP addresses)',
];
const FLOW_HEADERS = [
  'id',
  'srcWorkloadId',
  'dstWorkloadId',
  'srcIp',
  'dstIp',
  'srcPort',
  'dstPort',
  'protocol',
];

const escapeCell = (v: string | number | boolean | null | undefined): string =>
  v === null || v === undefined ? '""' : `"${String(v).replace(/"/g, '""')}"`;

const buildCsvString = <T>(
  headers: readonly string[],
  data: T[],
  rowMapper: (item: T) => (string | number | boolean | null | undefined)[],
): string =>
  [
    headers.join(','),
    ...data.map((item) => rowMapper(item).map(escapeCell).join(',')),
  ].join('\n');

const mapApplicationToCsvRow = (a: AimApplicationDraft) => [
  a.id,
  a.name,
  a.environment,
];

const mapWorkloadToCsvRow = (w: AimWorkloadDraft) => [
  w.id,
  w.applicationId,
  w.name,
  w.ipAddress,
  w.interfaces?.join(';') ?? '',
];

const mapFlowToCsvRow = (f: AimFlowDraft) => [
  f.id,
  f.srcWorkloadId,
  f.dstWorkloadId,
  f.srcIp,
  f.dstIp,
  f.srcPort,
  f.dstPort,
  f.protocol,
];

export const downloadAimExampleFiles = (): void => {
  const zip = new PizZip();
  zip.file(
    'AIM_Applications_Example.csv',
    buildCsvString(APP_HEADERS, EXAMPLE_APPS, mapApplicationToCsvRow),
  );
  zip.file(
    'AIM_Workloads_Example.csv',
    buildCsvString(WORKLOAD_HEADERS, EXAMPLE_WORKLOADS, mapWorkloadToCsvRow),
  );
  zip.file(
    'AIM_Flows_Example.csv',
    buildCsvString(FLOW_HEADERS, EXAMPLE_FLOWS, mapFlowToCsvRow),
  );

  const blob = zip.generate({ type: 'blob', mimeType: 'application/zip' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'AIM_Example_Data.zip';
  link.click();
  URL.revokeObjectURL(link.href);
};
